import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { FaMagic } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import BaseUrl from "../../ApiServices/BaseUrl";
import { JOB_SEEKER_CATEGORY_OPTIONS, COUNTRY_NATIONALITIES } from "../../../Constants/dropdownOptions";
import {
  buildSupportMobileNumber,
  normalizeCountryCode,
  splitPhoneNumber,
  validateSupportForm,
} from "../../../Constants/countryDialCodes";
import CountryCodeSelect from "../CountryCodeSelect/CountryCodeSelect";
import "./SupportContactForm.css";

const user = JSON.parse(localStorage.getItem("user")) || {};

const getPrefilledPhoneData = (currentUser) =>
  splitPhoneNumber(
    currentUser.mobile_number || currentUser.whatsapp_number,
    currentUser.mobile_number_country_code,
    currentUser.mobile_country_code,
    currentUser.whatsapp_country_code
  );

const defaultTemplates = [
  { label: "General Employer", text: "Hello, we are a company seeking skilled and qualified workers. We are interested in connecting with verified manpower agencies, exploring hiring options, and subscribing to GulfWorker services." },
  { label: "Domestic Worker Employer", text: "Hello, we are looking to hire housemaids, nannies, and caregivers. We would like to connect with trusted manpower agencies, review candidate profiles, and subscribe to GulfWorker for easy and fast hiring." },
  { label: "Subscription Inquiry", text: "Hello, we would like to know more about your subscription plans and how we can start hiring through GulfWorker." },
  { label: "Urgent Hiring - Skilled Workers", text: "Hello, I need skilled workers urgently for my company." },
  { label: "Urgent Hiring - Domestic Workers", text: "Hello, I need domestic workers urgently." },
  { label: "Standard Hiring", text: "Hello, I am looking to hire workers." },
  { label: "Bulk Hiring", text: "Hello, I require multiple workers for my company." },
];

const getDefaultUserRole = () => {
  const role = (user.user_type || user.user_role || user.role || "").toLowerCase();
  if (role.includes("job") || role.includes("seeker")) return "job_seeker";
  if (role.includes("employer")) return "employer";
  if (role.includes("agency")) return "agency";
  return "employer"; // Default changed to employer as requested
};

const SupportContactForm = ({
  className = "",
  title,
  compact = false,
  inlineFields = false,
  buttonClassName = "",
  templates = defaultTemplates,
}) => {
  const { t } = useTranslation();

  const resolvedTemplates = templates === defaultTemplates ? [
    { label: t("contact_form.templates.general.label"), text: t("contact_form.templates.general.text") },
    { label: t("contact_form.templates.domestic.label"), text: t("contact_form.templates.domestic.text") },
    { label: t("contact_form.templates.subscription.label"), text: t("contact_form.templates.subscription.text") },
    { label: t("contact_form.templates.urgent_skilled.label"), text: t("contact_form.templates.urgent_skilled.text") },
    { label: t("contact_form.templates.urgent_domestic.label"), text: t("contact_form.templates.urgent_domestic.text") },
    { label: t("contact_form.templates.standard.label"), text: t("contact_form.templates.standard.text") },
    { label: t("contact_form.templates.bulk.label"), text: t("contact_form.templates.bulk.text") },
  ] : templates;

  const initialPhone = getPrefilledPhoneData(user);
  const [formData, setFormData] = useState({
    name: user.first_name || "",
    email: user.email || "",
    mobile_country_code: initialPhone.countryCode || "",
    mobile_number: initialPhone.localNumber || "",
    message: "",
    user_role: getDefaultUserRole(),
    job_category: "",
    nationality: "",
    promotionalConsent: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showNatDropdown, setShowNatDropdown] = useState(false);
  const [isCodePopupShown, setIsCodePopupShown] = useState(false);
  const [natSearch, setNatSearch] = useState("");
  const natDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (natDropdownRef.current && !natDropdownRef.current.contains(e.target)) {
        setShowNatDropdown(false);
        setNatSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePhoneFocus = () => {
    if (!formData.mobile_country_code && !isCodePopupShown) {
      setIsCodePopupShown(true);
      setErrors((prev) => ({ ...prev, mobile_country_code: t("contact_form.error_selection_required") }));
      Swal.fire({
        title: t("contact_form.error_country_code_title"),
        text: t("contact_form.error_country_code_text"),
        icon: "warning",
        confirmButtonColor: "#3085d6",
      }).then(() => {
        setTimeout(() => setIsCodePopupShown(false), 500);
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : (name === "mobile_number" ? value.replace(/\D/g, "") : value);
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTemplateSelect = (text) => {
    setFormData((prev) => ({ ...prev, message: text }));
    setErrors((prev) => ({ ...prev, message: "" }));
    setShowTemplates(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateSupportForm(formData);

    if (!formData.promotionalConsent) {
      validationErrors.promotionalConsent = t("contact_form.error_consent_required");
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        mobile_country_code: normalizeCountryCode(formData.mobile_country_code),
        mobile_number: buildSupportMobileNumber(formData.mobile_country_code, formData.mobile_number),
      };

      const response = await fetch(`${BaseUrl}/supporttickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to submit form");

      Swal.fire({
        title: t("contact_form.success_title"),
        text: t("contact_form.success_text"),
        icon: "success",
        confirmButtonColor: "#3085d6",
      });

      setFormData((prev) => ({ ...prev, message: "", job_category: "", nationality: "", promotionalConsent: false }));
      setErrors({});
      setShowTemplates(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({ title: t("contact_form.error_title"), text: t("contact_form.error_text"), icon: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const wrapperClassName = ["support-contact-form", compact ? "support-contact-form-compact" : "", className].filter(Boolean).join(" ");

  const getPolicyLinks = () => {
    const roleMap = {
      job_seeker: "jobseeker",
      employer: "employer",
      agency: "agency",
    };
    const rolePath = roleMap[formData.user_role] || "jobseeker";
    return {
      terms: `/${rolePath}-terms-and-conditions`,
      privacy: `/${rolePath}-privacy-policy`,
    };
  };

  const policyLinks = getPolicyLinks();

  return (
    <div className={wrapperClassName}>
      {title ? <h5 className="support-contact-form-title">{title}</h5> : null}

      <Form onSubmit={handleSubmit} noValidate>
        {/* Field 1: User Role */}
        <Form.Group className="mb-3" controlId={compact ? "footerFormRole" : "formRole"}>
          <Form.Select name="user_role" value={formData.user_role} onChange={handleInputChange} isInvalid={Boolean(errors.user_role)} required>
            <option value="employer">{t("contact_form.role_employer")}</option>
            <option value="agency">{t("contact_form.role_agency")}</option>
            <option value="job_seeker">{t("contact_form.role_job_seeker")}</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">{errors.user_role}</Form.Control.Feedback>
        </Form.Group>

        {/* Field 2 & 3: Category & Nationality */}
        <Row className={compact ? "g-2" : ""}>
          <Col xs={12} md={compact && inlineFields ? 6 : compact ? 12 : 6}>
            <Form.Group className="mb-3" controlId={compact ? "footerFormJobCategory" : "formJobCategory"}>
              <Form.Select name="job_category" value={formData.job_category} onChange={handleInputChange} isInvalid={Boolean(errors.job_category)}>
                <option value="">{t("contact_form.select_category")}</option>
                {JOB_SEEKER_CATEGORY_OPTIONS.map((cat, idx) => (
                  <option key={idx} value={cat}>{cat}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.job_category}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12} md={compact && inlineFields ? 6 : compact ? 12 : 6}>
            <Form.Group className="mb-3" controlId={compact ? "footerFormNationality" : "formNationality"}>
              <div ref={natDropdownRef} style={{ position: 'relative' }}>
                <div
                  className={`form-select ${errors.nationality ? "is-invalid" : ""}`}
                  onClick={() => setShowNatDropdown(!showNatDropdown)}
                  style={{ cursor: 'pointer', color: formData.nationality ? (compact ? '#fff' : '#212529') : (compact ? '#aaa' : '#6c757d') }}
                >
                  {formData.nationality || t("contact_form.select_nationality")}
                </div>
                {showNatDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, background: compact ? '#1a1a1a' : '#fff', border: '1px solid #ced4da', borderRadius: '0.375rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: '2px', color: compact ? '#fff' : '#212529' }}>
                    <div style={{ padding: '8px' }}>
                      <input
                        type="text" className="form-control form-control-sm" placeholder={t("contact_form.search")} value={natSearch}
                        onChange={(e) => setNatSearch(e.target.value)} autoFocus
                        style={compact ? { background: '#2a2a2a', color: '#fff', border: '1px solid #555' } : {}}
                      />
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {COUNTRY_NATIONALITIES.filter(n => n.toLowerCase().includes(natSearch.toLowerCase())).map((nat, idx) => (
                        <div
                          key={idx} onClick={() => {
                            setFormData(prev => ({ ...prev, nationality: nat }));
                            setErrors(prev => ({ ...prev, nationality: "" }));
                            setShowNatDropdown(false);
                            setNatSearch("");
                          }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: compact ? '1px solid #333' : '1px solid #f0f0f0' }}
                          onMouseEnter={(e) => e.target.style.background = compact ? '#333' : '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          {nat}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {errors.nationality && <div className="invalid-feedback d-block">{errors.nationality}</div>}
            </Form.Group>
          </Col>
        </Row>

        {/* Field 4: Name */}
        <Form.Group className="mb-3" controlId={compact ? "footerFormName" : "formName"}>
          <Form.Control type="text" name="name" placeholder={t("contact_form.name_placeholder")} value={formData.name} onChange={handleInputChange} isInvalid={Boolean(errors.name)} required />
          <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
        </Form.Group>

        {/* Field 5 & 6: Phone */}
        <Row className={compact ? "g-2" : ""}>
          <Col xs={12} md={compact && inlineFields ? 6 : compact ? 12 : 6}>
            <Form.Group className="mb-3" controlId={compact ? "footerFormPhoneCode" : "formPhoneCode"}>
              <CountryCodeSelect
                value={formData.mobile_country_code}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, mobile_country_code: value }));
                  setErrors((prev) => ({ ...prev, mobile_country_code: "" }));
                }}
                error={errors.mobile_country_code}
                theme={compact ? "dark" : "light"}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={compact && inlineFields ? 6 : compact ? 12 : 6}>
            <Form.Group className="mb-3" controlId={compact ? "footerFormPhoneNumber" : "formPhoneNumber"}>
              <Form.Control
                type="text" name="mobile_number" inputMode="numeric"
                placeholder={t("contact_form.phone_placeholder")}
                value={formData.mobile_number} onChange={handleInputChange}
                onFocus={handlePhoneFocus}
                isInvalid={Boolean(errors.mobile_number)} required
              />
              <Form.Control.Feedback type="invalid">{errors.mobile_number}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        {/* Field 7: Email */}
        <Form.Group className="mb-3" controlId={compact ? "footerFormEmail" : "formEmail"}>
          <Form.Control type="email" name="email" placeholder={t("contact_form.email_placeholder")} value={formData.email} onChange={handleInputChange} isInvalid={Boolean(errors.email)} required />
          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
        </Form.Group>

        {/* Field 8: Message */}
        <Form.Group className="mb-3" controlId={compact ? "footerFormMessage" : "formMessage"}>
          <div className="support-contact-form-toolbar">
            <Form.Label className="mb-0">{t("contact_form.message_label")}</Form.Label>
            <Button variant="outline-primary" size="sm" type="button" onClick={() => setShowTemplates((prev) => !prev)} className="auto-desc-btn">
              <FaMagic className="me-1" /> {t("contact_form.auto_description")}
            </Button>
          </div>
          {showTemplates && (
            <div className="message-templates mb-2">
              {resolvedTemplates.map((template, index) => (
                <div key={index} className="template-option" onClick={() => handleTemplateSelect(template.text)}>
                  <strong>{template.label}</strong>
                  <p className="mb-0 text-muted small">{template.text}</p>
                </div>
              ))}
            </div>
          )}
          <Form.Control as="textarea" rows={compact ? 3 : 4} name="message" placeholder={t("contact_form.message_placeholder")} value={formData.message} onChange={handleInputChange} isInvalid={Boolean(errors.message)} required />
          <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
        </Form.Group>

        {/* Promotional Messages Consent Checkbox */}
        <Form.Group className="mb-3" controlId={compact ? "footerFormPromotional" : "formPromotional"}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              name="promotionalConsent"
              checked={formData.promotionalConsent}
              onChange={handleInputChange}
              style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px', flexShrink: 0 }}
              id="promotionalCheckbox"
            />
            <label htmlFor="promotionalCheckbox" style={{ margin: 0, cursor: 'pointer', userSelect: 'none', fontSize: compact ? '12px' : '13px' }}>
              {t("contact_form.consent_part1")}{" "}
              <a
                href={policyLinks.terms}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0056b3', textDecoration: 'none', fontWeight: '600' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {t("contact_form.terms_link")}
              </a>
              {" "}{t("contact_form.consent_part2")}{" "}
              <a
                href={policyLinks.privacy}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0056b3', textDecoration: 'none', fontWeight: '600' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {t("contact_form.privacy_link")}
              </a>
              .
            </label>
          </div>
          {errors.promotionalConsent && <div className="invalid-feedback d-block" style={{ marginTop: '5px' }}>{errors.promotionalConsent}</div>}
        </Form.Group>

        <Button
          variant="primary"
          type="submit"
          className={buttonClassName || "support-contact-submit-btn"}
          disabled={isSubmitting || !formData.promotionalConsent}
          title={!formData.promotionalConsent ? t("contact_form.tooltip_consent") : ""}
        >
          {isSubmitting ? t("contact_form.sending") : t("contact_form.submit")}
        </Button>
      </Form>
    </div>
  );
};

export default SupportContactForm;
